import {
  type BikeType,
  defaultFrameContractData20 as defaultFrameContract,
  type FrameContractDTO,
} from "@jobrad-gmbh/bike-leasing-calculator";
import { hasMultipleServiceOptions, isCompanyServicePayer, isServiceMandatory } from "../../../api";
import { type ActiveView, ConfigurationActionTypes } from "./actions";
import { AllServiceValuesType, AppAction, CalculatorEnv, getInspectionType } from "..";
import { InputDataUIModel } from "../../../components";

type BikeLeasingSetting = {
  taxationFactor: number;
  residualFactor: number;
};

type ElementVisibilityType = Record<
  | "insuranceSelect"
  | "serviceSelect"
  | "serviceSelectHintEmployerPays"
  | "individualSubsidyInput"
  | "subsidyDetails"
  | "subsidyBreakdownRow"
  | "overallUsageRate"
  | "nvstSelect"
  | "servicePayerSelect"
  | "bottomSheetCtaButton"
  | "arbeitnehmerTypSelect"
  | "krankenversicherungSelect"
  | "vormieteInfo",
  boolean
>;

type Configuration = {
  frameContract: FrameContractDTO;
  leasingSetting: BikeLeasingSetting;
  elementVisibility: ElementVisibilityType;
  elementVisibilityOverrides: Partial<ElementVisibilityType>;
  isExpertMode: boolean;
  calculatorEnv: CalculatorEnv;
  ctaUrl: string | undefined;
  showHeader: boolean;
  showBudgetCalculator: boolean;
  deductGrantFromServicePrices: boolean;
  activeView: ActiveView;
};

const initialConfiguration: Configuration = {
  frameContract: defaultFrameContract,
  leasingSetting: {
    taxationFactor: getBikeTaxationFactor(defaultFrameContract),
    residualFactor: getResidualRateFactor(defaultFrameContract),
  },
  elementVisibility: {
    serviceSelect: false,
    serviceSelectHintEmployerPays: false,
    individualSubsidyInput: false,
    subsidyDetails: false,
    subsidyBreakdownRow: true,
    overallUsageRate: true,
    insuranceSelect: false,
    nvstSelect: false,
    servicePayerSelect: false,
    bottomSheetCtaButton: false,
    arbeitnehmerTypSelect: false,
    krankenversicherungSelect: false,
    vormieteInfo: false,
  },
  elementVisibilityOverrides: {},
  isExpertMode: false,
  calculatorEnv: "website",
  ctaUrl: "",
  showHeader: false,
  showBudgetCalculator: false,
  deductGrantFromServicePrices: false,
  activeView: "leasing",
};

const configurationReducer = (configuration: Configuration, action: AppAction): Configuration => {
  switch (action.type) {
    case ConfigurationActionTypes.SET_FRAME_CONTRACT: {
      const { frameContract, calculatorEnv, ctaUrl, showHeader, showBudgetCalculator, presentationOverrides } =
        action.payload;
      const leasingSetting = {
        taxationFactor: getBikeTaxationFactor(frameContract),
        residualFactor: getResidualRateFactor(frameContract),
      };

      const elementVisibility = applyElementVisibilityOverrides(
        getElementVisibilities(configuration.elementVisibility, frameContract, calculatorEnv),
        presentationOverrides?.elementVisibility,
      );

      // Compute final showHeader value: use explicit value if provided, otherwise default based on environment
      const resolvedShowHeader = showHeader ?? calculatorEnv === "dealer";

      // Show budget calculator if attribute is set, or frame contract flag is true
      const resolvedShowBudgetCalculator =
        showBudgetCalculator ?? frameContract.portal_custom_content_show_future_payment_calculator ?? false;

      return {
        ...configuration,
        frameContract,
        leasingSetting,
        elementVisibility,
        elementVisibilityOverrides: presentationOverrides?.elementVisibility ?? {},
        calculatorEnv,
        ctaUrl,
        showHeader: resolvedShowHeader,
        showBudgetCalculator: resolvedShowBudgetCalculator,
        deductGrantFromServicePrices: presentationOverrides?.deductGrantFromServicePrices ?? false,
        activeView: "leasing",
      };
    }

    case ConfigurationActionTypes.UPDATE_ELEMENT_VISIBILITIES: {
      const elementVisibility = getElementVisibilities(
        configuration.elementVisibility,
        configuration.frameContract,
        configuration.calculatorEnv,
        action.payload.calculatorInput,
      );
      return {
        ...configuration,
        elementVisibility: applyElementVisibilityOverrides(elementVisibility, configuration.elementVisibilityOverrides),
      };
    }

    case ConfigurationActionTypes.TOGGLE_EXPERT_MODE: {
      return { ...configuration, isExpertMode: action.payload };
    }

    case ConfigurationActionTypes.SET_ACTIVE_VIEW: {
      return { ...configuration, activeView: action.payload };
    }

    default:
      return configuration;
  }
};

export function getResidualRateFactor(frameContract: FrameContractDTO): number {
  if (frameContract.use_custom_residual_factor) {
    return frameContract.custom_residual_factor;
  } else {
    return frameContract.leasing_company_id.residual_factor_customer;
  }
}

export function getBikeTaxationFactor(frameContract: FrameContractDTO, fahrradtyp: BikeType = "Fahrrad"): number {
  if (fahrradtyp !== "S-Pedelec") {
    return frameContract.contract_type.fcs_taxation_factor;
  } else {
    return frameContract.contract_type.fcs_taxation_factor_special;
  }
}

const getElementVisibilities = (
  elementVisibilityConfig: ElementVisibilityType,
  frameContract: FrameContractDTO,
  calculatorEnv: CalculatorEnv,
  calculatorInput?: Partial<InputDataUIModel>,
) => {
  return {
    ...elementVisibilityConfig,
    serviceSelect: shouldDisplayServiceSelect(frameContract),
    serviceSelectHintEmployerPays:
      calculatorEnv !== "website" &&
      calculatorEnv !== "dealer" &&
      isCompanyServicePayer(frameContract) &&
      hasMultipleServiceOptions(frameContract),
    individualSubsidyInput: frameContract.grant_type === "individual",
    subsidyDetails: calculatorEnv !== "website" && calculatorEnv !== "dealer",
    subsidyBreakdownRow: true,
    overallUsageRate: true,
    insuranceSelect: frameContract.insurance_option_id.payer === "selectable",
    servicePayerSelect: shouldDisplayServicePayerSelection(
      calculatorEnv,
      getInspectionType(calculatorInput?.serviceType ?? false, calculatorInput?.serviceSubType ?? false),
    ),
    nvstSelect: frameContract.use_input_tax_deduction === "select",
    arbeitnehmerTypSelect: frameContract.calculator_type === "arbeitnehmer_beamte",
    krankenversicherungSelect: calculatorInput?.arbeitnehmerTyp === "angestellte",
    vormieteInfo: frameContract.use_pre_rate_calculations,
  };
};

const applyElementVisibilityOverrides = (
  elementVisibility: ElementVisibilityType,
  overrides?: Partial<ElementVisibilityType>,
): ElementVisibilityType => ({
  ...elementVisibility,
  ...overrides,
});

const shouldDisplayServiceSelect = (frameContract: FrameContractDTO) => {
  // defaut case: employee is service payer and there are multiple service options for selection
  const isDefaultCase = !isCompanyServicePayer(frameContract) && hasMultipleServiceOptions(frameContract);

  // edge case 1 - show selector if: company is service payer and there are multiple service options for selection
  const isEdgeCase1 = isCompanyServicePayer(frameContract) && hasMultipleServiceOptions(frameContract);

  // edge case 2 - dont show selector if: employee is service payer, service selection is mandatory and there is only one service option
  const isEdgeCase2 =
    !isCompanyServicePayer(frameContract) &&
    isServiceMandatory(frameContract) &&
    !hasMultipleServiceOptions(frameContract);

  return (isDefaultCase || isEdgeCase1) && !isEdgeCase2;
};

const shouldDisplayServicePayerSelection = (calculatorEnv: CalculatorEnv, serviceType: AllServiceValuesType | null) =>
  (calculatorEnv === "website" || calculatorEnv === "dealer") && serviceType !== null;

const useBasepriceForResidual = (state: Configuration) => {
  return state.frameContract.calculation_base_price;
};

export {
  type Configuration,
  type ElementVisibilityType,
  configurationReducer,
  initialConfiguration,
  useBasepriceForResidual,
};
